import {
  ConferenceBrandingApiError,
  validConferenceWebsiteUrl,
  type ConferenceBrandingAssetKind,
  type ConferenceDetail,
} from "./portfolio-overview-api.ts";
import type { UserRole } from "./auth-types.ts";
import { portfolioErrorMessageKey } from "./portfolio-api-errors.ts";
import { translate } from "./translations.ts";

export const CONFERENCE_BRANDING_MAX_BYTES = 5_242_880;
export const CONFERENCE_BRANDING_MIN_DIMENSION = 16;
export const CONFERENCE_BRANDING_MAX_DIMENSION = 4_096;
export const CONFERENCE_BRANDING_MAX_PIXELS = 16_777_216;
export const CONFERENCE_BRANDING_ACCEPT =
  "image/png,image/jpeg,image/webp,image/svg+xml";

const acceptedTypes = new Set(CONFERENCE_BRANDING_ACCEPT.split(","));

export type ConferenceBrandingFileError =
  | "empty_file"
  | "file_too_large"
  | "icon_aspect_ratio"
  | "invalid_dimensions"
  | "unsupported_type";

export interface ConferenceBrandingDimensions {
  readonly width: number;
  readonly height: number;
}

export function canManageConferenceBranding(role: UserRole): boolean {
  return role === "admin";
}

export function conferenceWebsiteDraftValue(
  conference: Pick<
    ConferenceDetail,
    "projectId" | "websiteUrl" | "websiteUrlOverride"
  >,
): string {
  if (conference.websiteUrlOverride !== undefined)
    return conference.websiteUrlOverride ?? "";
  return conference.websiteUrl.replace(/\/$/, "") ===
    `https://${conference.projectId}.web.app`
    ? ""
    : conference.websiteUrl;
}

export function normalizeConferenceWebsiteInput(
  value: string,
): string | null | undefined {
  // Undefined is invalid; null is the deliberate reset to the default website.
  if (/[\\\p{Cc}\p{Cf}]/u.test(value)) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return validConferenceWebsiteUrl(trimmed) ? trimmed : undefined;
}

export function applyConferenceWebsiteUpdate(
  conference: ConferenceDetail,
  websiteUrlOverride: string | null,
): ConferenceDetail {
  return {
    ...conference,
    websiteUrlOverride,
    websiteUrl: websiteUrlOverride ?? `https://${conference.projectId}.web.app`,
  };
}

export function validateConferenceBrandingFile(
  image: Pick<Blob, "size" | "type">,
): ConferenceBrandingFileError | null {
  if (image.size === 0) return "empty_file";
  if (image.size > CONFERENCE_BRANDING_MAX_BYTES) return "file_too_large";
  if (!acceptedTypes.has(image.type)) return "unsupported_type";
  return null;
}

export function validateConferenceBrandingDimensions(
  kind: ConferenceBrandingAssetKind,
  dimensions: ConferenceBrandingDimensions,
): ConferenceBrandingFileError | null {
  const { width, height } = dimensions;
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width < CONFERENCE_BRANDING_MIN_DIMENSION ||
    height < CONFERENCE_BRANDING_MIN_DIMENSION ||
    width > CONFERENCE_BRANDING_MAX_DIMENSION ||
    height > CONFERENCE_BRANDING_MAX_DIMENSION ||
    width * height > CONFERENCE_BRANDING_MAX_PIXELS
  ) {
    return "invalid_dimensions";
  }
  const aspectRatio = width / height;
  if (kind === "icon" && (aspectRatio < 0.8 || aspectRatio > 1.25)) {
    return "icon_aspect_ratio";
  }
  return null;
}

function positiveSvgLength(value: string | null): number | null {
  if (!value) return null;
  const match = /^\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*$/i.exec(value);
  const parsed = match ? Number(match[1]) : Number.NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

async function svgDimensions(
  file: File,
): Promise<ConferenceBrandingDimensions> {
  const source = await file.text();
  const document = new DOMParser().parseFromString(source, "image/svg+xml");
  const svg = document.documentElement;
  if (svg.localName !== "svg" || document.querySelector("parsererror")) {
    throw new Error("invalid_svg");
  }
  const width = positiveSvgLength(svg.getAttribute("width"));
  const height = positiveSvgLength(svg.getAttribute("height"));
  if (width && height) return { width, height };
  const viewBox = (svg.getAttribute("viewBox") ?? "")
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (
    viewBox.length === 4 &&
    Number.isFinite(viewBox[2]) &&
    Number.isFinite(viewBox[3]) &&
    Number(viewBox[2]) > 0 &&
    Number(viewBox[3]) > 0
  ) {
    return { width: Number(viewBox[2]), height: Number(viewBox[3]) };
  }
  throw new Error("missing_svg_dimensions");
}

export async function readConferenceBrandingDimensions(
  file: File,
): Promise<ConferenceBrandingDimensions> {
  if (file.type === "image/svg+xml") return svgDimensions(file);
  if ("createImageBitmap" in globalThis) {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  }
  return new Promise((resolve, reject) => {
    const source = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(source);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(source);
      reject(new Error("invalid_image"));
    };
    image.src = source;
  });
}

export function effectiveConferenceBrandingUrl(
  conference: Pick<
    ConferenceDetail,
    | "brandingOverride"
    | "effectiveIconUrl"
    | "effectiveLogoUrl"
    | "iconUrl"
    | "logoUrl"
  >,
  kind: ConferenceBrandingAssetKind,
): string | null {
  if (kind === "icon") {
    return (
      conference.brandingOverride?.icon?.url ??
      conference.effectiveIconUrl ??
      conference.iconUrl ??
      conference.logoUrl
    );
  }
  return (
    conference.brandingOverride?.logo?.url ??
    conference.effectiveLogoUrl ??
    conference.logoUrl
  );
}

export function conferenceBrandingFileErrorMessage(
  code: ConferenceBrandingFileError,
): string {
  if (code === "file_too_large") {
    return "Choose an image no larger than 5 MiB.";
  }
  if (code === "unsupported_type") {
    return "Choose a PNG, JPEG, WebP or SVG image.";
  }
  if (code === "icon_aspect_ratio") {
    return "Choose a near-square icon with an aspect ratio between 0.8 and 1.25.";
  }
  if (code === "invalid_dimensions") {
    return "Choose an image between 16 and 4096 px on each side and no larger than 16.7 megapixels.";
  }
  return "The selected image is empty. Choose a different file.";
}

export function conferenceBrandingMutationErrorMessage(error: unknown): string {
  if (error instanceof ConferenceBrandingApiError) {
    if (error.code === "file_too_large") {
      return "The image exceeds the 5 MiB upload limit.";
    }
    if (error.code === "invalid_image") {
      return "The image was rejected. Check its format, dimensions and safe SVG content, then try again.";
    }
    if (error.code === "conference_not_found") {
      return "This conference is no longer available. Refresh the page before trying again.";
    }
    if (error.code === "storage_unavailable") {
      return "Branding storage is temporarily unavailable. Try again later.";
    }
  }
  return translate(
    "en",
    portfolioErrorMessageKey(error, "branding.error.saveFailed"),
  );
}
